import {firstValueFrom, Subscription} from 'rxjs';
import {AuthActions, AuthState, IUser} from '../../models/auth';
import {ClinicActions, ClinicState, IClinic} from '../../models/clinic';
import {IMenu} from '../../components/dropdown-menu/dropdown-menu.component';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {UserState} from '../../models/user';
import {PanelUtilitiesActions, PanelUtilitiesState} from '../../models/panel-utilities';
import {StorageService} from '../../services/storage/storage.service';
import {Store} from '@ngxs/store';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {AppTourService} from '../../services/app-tour/app-tour.service';
import {Loader} from '../../models/loader/loader.redux';
import {Logger} from './logger.class';
import {PatientAction} from '../../models/patient';
import {PatientSphereAction} from '../../models/patient-sphere';

export class MainMenuHelperClass {
  $user: Subscription;
  user: IUser;
  $clinic: Subscription;
  $active: Subscription;
  clinic: IClinic;
  $userClinics: Subscription;
  userClinics: Partial<IClinic>[];
  userLogged: IUser;
  active = false;
  delayInstance;

  menu: IMenu[] = [];

  params: Params;

  keepMenuActive = false;

  constructor(
    public storageService: StorageService,
    public router: Router,
    public store: Store,
    public utilityService: UtilitiesService,
    public activatedRoute: ActivatedRoute,
    public appTourService: AppTourService
  ) {
    this.initObservables();
  }

  initObservables(): void {
    this.$user = this.store.select(AuthState.getUser).subscribe((user) => {
      this.user = user;
      this.initMenuItems();
    });

    this.$clinic = this.store.select(ClinicState.getClinicSelected).subscribe((clinic) => {
      this.clinic = clinic;
      this.initMenuItems();
    });

    this.$userClinics = this.store.select(UserState.getUserClinics).subscribe((userClinics) => {
      this.userClinics = userClinics;
    });

    this.$active = this.store.select(PanelUtilitiesState.getMainMenuActive).subscribe(async (isActive) => {
      this.active = !!isActive;
    });

  }


  initMenuItems(): void {
    if (!this.clinic || !this.user) return;
    try {
      this.menu = [
        {
          name: 'Agenda',
          subItems: [
            {
              name: 'Mi semana', iconClass: 'fa-solid fa-calendar-week',
              visible: this.user.experts?.length > 0 || this.user.patients?.length > 0,
              url: `/clinic/${this.clinic._id}/summary`
            },
            {
              name: 'Mi agenda', iconClass: 'fa-solid fa-calendar-days',
              visible: this.user.experts?.length > 0 || this.user.patients === void 0,
              url: `/clinic/${this.clinic._id}/calendar/me`
            },
            {
              name: 'Mis pacientes', iconClass: 'fa-solid fa-users',
              visible: this.user.patients === void 0 && this.user.experts?.length > 0,
              url: `/clinic/${this.clinic._id}/patients/list`, params: { page: 1 }
            },
          ],
        },
        {
          name: 'Alimentos',
          visible: this.user.patients === void 0 && this.user.experts?.length > 0,
          subItems: [
            {
              name: 'Mis recetas', iconClass: 'fa-solid fa-apple-whole',
              url: `/clinic/${this.clinic._id}/food-groups/list`, params: { page: 1 }
            },
            {
              name: 'Mis planes de alimentación', iconClass: 'fa-solid fa-utensils',
              url: `/clinic/${this.clinic._id}/meal-plans/list`, params: { page: 1 }
            },
          ],
        },
        {
          name: 'Marketplace',
          visible: this.user.patients === void 0 && this.user.experts?.length > 0,
          subItems: [
            {
              name: 'Mi perfil público', iconClass: 'fa-solid fa-address-card',
              visible: this.user.experts?.length > 0,
              url: `/clinic/${this.clinic._id}/profile/expert/me/public`
            },
            // {
            //   name: 'Mis solicitudes de cita', iconClass: 'fa-solid fa-calendar-check',
            //   visible: this.user.experts?.length > 0,
            //   url: `/clinic/${this.clinic._id}/marketplace/appointment-requests`
            // },
          ],
        },
        {
          name: 'Configuración',
          subItems: [
            {
              name: 'Estadísticas', iconClass: 'fa-solid fa-gauge-simple',
              visible: this.user._id === this.clinic.ownerId && this.user.experts?.length > 0,
              url: `/clinic/${this.clinic._id}/dashboard`
            },
            { name: 'Mi perfil', iconClass: 'fa-solid fa-id-badge', url: this.getProfileURL() },
            {
              name: 'Administración', iconClass: 'fa-solid fa-toolbox',
              visible: this.user._id === this.clinic.ownerId,
              url: `/clinic/${this.clinic._id}/administration/general`
            },
          ],
        },
        {
          name: 'Expediente',
          visible: this.user.patients?.length > 0 && this.user.patients[0] !== void 0,
          subItems: [
            {
              name: 'Mi expediente', iconClass: 'fa-solid fa-folder-open',
              url: `/clinic/${this.clinic._id}/patients/${this.user.patients ? this.user.patients[0]?._id + '/' : ''}info`
            }
          ]
        }

      ];
    } catch (e) {
      console.log(e);
    }
  }

  /**
   * If the status is true, set a timeout to set the active property to true. If the status is false, set the active property to false
   * @param status - boolean - This is the status of the menu. If it's true, the menu will be shown. If it's false, the menu will be
   * hidden.
   */
  setMenuStatus(status: boolean): void {
    if (status === void 0) {
      return;
    }
    clearTimeout(this.delayInstance);
    if (this.appTourService.requiredMainMenu || this.keepMenuActive) {
      this.active = true;
      return;
    }

    if (!status) {
      this.active = false;
      return;
    }
    this.delayInstance = setTimeout(() => {
      if (status) {
        this.active = true;
      } else if (!status && this.keepMenuActive) {
        this.active = true;
        this.appTourService.showTour('dashboard-expert').then();
      }
    }, 500);
  }


  /**
   * It gets the user from the local storage, gets the clinicId from the URL, and dispatches an action to get the clinic from the server
   */
  async initValuesHttp(): Promise<void> {
    this.userLogged = this.storageService.getLocalItem<{
      token: string;
      user: IUser;
    }>('user')?.user;
    this.params = this.utilityService.getAllParams(this.activatedRoute);
    if (this.params && this.params.clinicId) {
      await firstValueFrom((this.store.dispatch(new ClinicActions.Get(this.params.clinicId))));
    }
  }



  /**
   * It logs out the user.
   */
  async onLogout(): Promise<void> {
    try {
      this.appTourService?.closeTour();
    } catch (e) {

    }
    try {
      await firstValueFrom((this.store.dispatch(new Loader(true, 'Cerrando sesión...'))));
      await firstValueFrom((this.store.dispatch(new AuthActions.Logout())));
      await firstValueFrom(this.store.dispatch(new PatientSphereAction.ShowSphereBubble(false, null, null)));
      window.location.reload();
    } catch (e) {
      Logger.log('Ocurrió un error', e);
    } finally {
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    }
  }



  /**
   * If the user is logged in, and the user is an expert, return the expert profile URL. If the user is logged in, and the user is a patient,
   * return the patient profile URL. Otherwise, return an empty string
   * @returns The URL of the profile page of the user logged in.
   */
  getProfileURL(): string {
    this.userLogged = this.storageService.getLocalItem<{
      token: string;
      user: IUser;
    }>('user')?.user;
    if (!this.clinic) return '';
    if (this.userLogged.experts && this.userLogged.experts[0]) {
      return `/clinic/${this.clinic._id}/profile/expert/me`;
    }
    if (this.userLogged.patients && this.userLogged.patients[0]) {
      return `/clinic/${this.clinic._id}/profile/patient/me`;
    }
    return '';
  }


  /**
   * It gets the current clinic, if the clinicId is different from the current clinicId, it loads the new clinic, loads the user, loads the
   * patient (if the user is a patient), and navigates to the new clinic
   * @param clinicId - The id of the clinic to which you want to go.
   */
  async onGoToClinic(clinicId): Promise<void> {
    try {

      if (clinicId && clinicId !== this.clinic._id) {
        await firstValueFrom((this.store.dispatch(new Loader(true, 'Cambiando...'))));
        await firstValueFrom((this.store.dispatch(new ClinicActions.Get(clinicId))));
        await firstValueFrom((this.store.dispatch(new AuthActions.GetUserMe(clinicId))));
        const user = this.storageService.getLocalItem<{
          token: string;
          user: IUser;
        }>('user')?.user;
        const isPatient = !!user?.patients;
        if (isPatient) {
          await firstValueFrom((this.store.dispatch(new PatientAction.GetById(user?.patients[0]._id, clinicId))));
        }
        await this.router.navigateByUrl(`clinic/${clinicId}/summary`);
      }
    } catch (e) {
      console.error('[onGoToClinic]', e);
    }
  }

  async onKeepMenuActive(isVisible: boolean): Promise<void> {
    this.keepMenuActive = isVisible;
  }
}
